'use client';

import { CABINA_PRIMARIA_PLACEHOLDER, isValidCabinaPrimaria } from '@slims/shared/constants';
import { Stack } from '@slims/ui/atoms';
import { EmptyCard } from '@slims/ui/molecules';
import { BatteryCharging } from 'lucide-react';
import { Field, Grid2, Section, TextInput } from '@/components/admin/detail-fields';
import { useMeterDraft } from '@/components/members/use-meter-draft';
import type { Meter } from '@/hooks/use-meters';

interface AccumuloTabProps {
	meter?: Meter;
	onUpdate: (id: string, patch: Record<string, unknown>) => Promise<unknown>;
	/** Registra il salvataggio di questo tab, così il "Salva" della pagina può invocarlo. */
	onRegisterSave?: (save: () => Promise<boolean>) => void;
	/** Il misuratore ha modifiche non salvate. */
	onDirtyChange?: (dirty: boolean) => void;
}

const NUMERIC = new Set(['capacitaKwh', 'potenzaKw', 'efficienzaCarica', 'efficienzaScarica']);

/** Tab 4 — Sistemi di accumulo (Misuratore M3 / batteria). Opzionale. */
export function AccumuloTab({ meter, onUpdate, onRegisterSave, onDirtyChange }: AccumuloTabProps) {
	const d = useMeterDraft(meter, onUpdate, NUMERIC, onRegisterSave, onDirtyChange);

	if (!meter) {
		// L'accumulo non è un ruolo ma un'attrezzatura: lo dichiara la spunta in
		// Anagrafica, e il punto di misura nasce da lì.
		return (
			<EmptyCard
				icon={<BatteryCharging />}
				message="Il punto di accumulo (M3) nasce dalla spunta «Sistema di accumulo» in Anagrafica: attivala e comparirà qui."
			/>
		);
	}

	return (
		<Stack gap="lg">
			<Section title="Misuratore M3 · Accumulo" hint="Sistema di storage e relativo misuratore">
				<Field label="Codice misuratore">
					<TextInput value={d.str('codice')} onChange={(x) => d.set('codice', x)} uppercase />
				</Field>
				<Grid2>
					<Field label="Capacità nominale (kWh)">
						<TextInput
							type="number"
							value={d.str('capacitaKwh')}
							onChange={(x) => d.set('capacitaKwh', x)}
						/>
					</Field>
					<Field label="Potenza nominale (kW)" hint="Quanta potenza la batteria può dare o assorbire.">
						<TextInput
							type="number"
							value={d.str('potenzaKw')}
							onChange={(x) => d.set('potenzaKw', x)}
						/>
					</Field>
					<Field
						label="Cabina primaria"
						hint={`Formato GSE: ${CABINA_PRIMARIA_PLACEHOLDER}`}
						error={
							isValidCabinaPrimaria(d.str('cabinaPrimaria'))
								? undefined
								: `Formato non valido: usa un codice come ${CABINA_PRIMARIA_PLACEHOLDER}`
						}
					>
						<TextInput
							value={d.str('cabinaPrimaria')}
							onChange={(x) => d.set('cabinaPrimaria', x)}
							placeholder={CABINA_PRIMARIA_PLACEHOLDER}
							uppercase
						/>
					</Field>
					<Field label="Efficienza di carica (0-1)">
						<TextInput
							type="number"
							value={d.str('efficienzaCarica')}
							onChange={(x) => d.set('efficienzaCarica', x)}
						/>
					</Field>
					<Field label="Efficienza di scarica (0-1)">
						<TextInput
							type="number"
							value={d.str('efficienzaScarica')}
							onChange={(x) => d.set('efficienzaScarica', x)}
						/>
					</Field>
				</Grid2>
			</Section>
		</Stack>
	);
}
